import { gql } from "@apollo/client";
import CiroTableCell from "../../../shared/CiroTable/CiroTableCell";
import CiroTableRow from "../../../shared/CiroTable/CiroTableRow";
import classNames from "classnames";
import {
  CiroOrgContactCellInfo_OrgContactFragmentDoc,
  ContactListContactRow_ContactFragment,
} from "../../../../__generated__/graphql";
import {
  useFragment as getFragmentData,
  useFragment,
} from "../../../../__generated__";
import CiroCheckbox from "../../../shared/CiroCheckbox";
import AutomationListFindEmailsInlineButton, {
  AutomationListFindEmailsInlineButton_OrgContact,
} from "../../../automation/shared/AutomationListFindEmailsInlineButton";
import AutomationListFindNumbersInlineButton, {
  AutomationListFindNumbersInlineButton_OrgContact,
} from "../../../automation/shared/AutomationListFindNumbersInlineButton";
import CopyButton from "../../../automation/shared/CopyButton";
import CiroOrgContactCellInfo, {
  CiroOrgContactCellInfo_OrgContact,
} from "../../../shared/CiroTable/CiroOrgContactCellInfo";
import CiroLinkedInCompanyCellInfo, {
  LinkedInCompanyCellInfo_LinkedInCompany,
} from "../../../shared/CiroTable/LinkedInCompanyCellInfo";
import { useContactInfoContext } from "../../../../reactHooks/autopilotList/useContactInfoContext";

export const ContactListContactRow_Contact = gql`
  fragment ContactListContactRow_Contact on ContactListSavedContact {
    id
    org_contact {
      ...CiroOrgContactCellInfo_OrgContact
      id
      company_name
      linkedInCompany {
        ...LinkedInCompanyCellInfo_LinkedInCompany
      }
      orgPhoneNumbers {
        phone_number
      }
      orgEmails {
        email
      }
      mostRecentPhoneNumberRequestTransaction {
        id
        finished_at
      }
      mostRecentEmailRequestTransaction {
        id
        finished_at
      }
      ...AutomationListFindEmailsInlineButton_OrgContact
      ...AutomationListFindNumbersInlineButton_OrgContact
    }
  }
  ${AutomationListFindEmailsInlineButton_OrgContact}
  ${AutomationListFindNumbersInlineButton_OrgContact}
  ${CiroOrgContactCellInfo_OrgContact}
  ${LinkedInCompanyCellInfo_LinkedInCompany}
`;

interface IContactListContactRowProps {
  contactListId: number | null;
  contact: ContactListContactRow_ContactFragment;
  isSelected: boolean;
  onSelect: () => void;
  isLastTwoRows: boolean;
}

const ContactListContactRow = ({
  contactListId,
  contact,
  isSelected,
  onSelect,
}: IContactListContactRowProps) => {
  const { getContactInfo } = useContactInfoContext();
  const orgContactId = contact.org_contact?.id;

  /**
   * Getting this phone/email data from a context is an ANTI-PATTERN.
   *
   * Normally we should be getting it from an Apollo fragment. There is some bug
   * we have not been able to solve with updating the Apollo cache for OrgContacts
   * after phoneRequestTransactions and emailRequestTransactions are completed, so
   * we are using a context in this case.
   *
   * Do NOT replicate this pattern in other parts of the codebase.
   */
  const contextData = orgContactId ? getContactInfo(orgContactId) : null;
  const phoneNumber = contextData?.phoneNumbers[0] ?? contact.org_contact?.orgPhoneNumbers[0]?.phone_number;
  const phoneTransaction = contextData?.mostRecentPhoneRequestTransaction ?? contact.org_contact?.mostRecentPhoneNumberRequestTransaction;
  const showPhoneNotFound = !phoneNumber && phoneTransaction?.finished_at;

  const email = contextData?.emails[0] ?? contact.org_contact?.orgEmails[0]?.email;
  const emailTransaction = contextData?.mostRecentEmailRequestTransaction ?? contact.org_contact?.mostRecentEmailRequestTransaction;
  const showEmailNotFound = !email && emailTransaction?.finished_at;
  const cellInfoOrgContact = useFragment(
    CiroOrgContactCellInfo_OrgContactFragmentDoc,
    contact.org_contact,
  );

  return (
    <CiroTableRow clickable={false}>
      <CiroTableCell padding={2} minWidth="50px">
        <div className={classNames("pl-2")}>
          <CiroCheckbox checked={isSelected} onClick={onSelect} />
        </div>
      </CiroTableCell>
      <CiroTableCell
        className={classNames("align-top", "whitespace-normal")}
        minWidth="200px"
      >
        <CiroOrgContactCellInfo orgContact={cellInfoOrgContact} />
      </CiroTableCell>
      <CiroTableCell className={classNames("align-top")} minWidth="150px">
        <CiroLinkedInCompanyCellInfo
          linkedInCompany={getFragmentData(
            LinkedInCompanyCellInfo_LinkedInCompany,
            contact.org_contact?.linkedInCompany,
          )}
          companyName={contact.org_contact?.company_name ?? ""}
        />
      </CiroTableCell>
      <CiroTableCell minWidth="200px" className={classNames("align-top")}>
        <div className={classNames("flex", "flex-col", "gap-1")}>
          {phoneNumber ? (
            <div className={classNames("flex", "items-center", "gap-2")}>
              <span title={phoneNumber} className={classNames("min-w-32")}>
                {phoneNumber}
              </span>
              <CopyButton
                analyticsField="Copy phone number from contact list"
                textToCopy={phoneNumber}
              />
            </div>
          ) : showPhoneNotFound ? (
            <span className={classNames("text-neutral-400")}>
              Phone not found
            </span>
          ) : (
            <AutomationListFindNumbersInlineButton
              contactListId={contactListId}
              orgContact={contact.org_contact}
            />
          )}
          {email ? (
            <div className={classNames("flex", "items-center", "gap-2")}>
              <span title={email}>
                {email.length > 30 ? `${email.slice(0, 30)}...` : email}
              </span>
              <CopyButton
                analyticsField="Copy email from contact list"
                textToCopy={email}
              />
            </div>
          ) : showEmailNotFound ? (
            <span className={classNames("text-neutral-400")}>
              Email not found
            </span>
          ) : (
            <AutomationListFindEmailsInlineButton
              contactListId={contactListId}
              orgContact={contact.org_contact}
            />
          )}
        </div>
      </CiroTableCell>
    </CiroTableRow>
  );
};

export default ContactListContactRow;
